<script setup lang="ts">
import type { SwiperOptions } from 'swiper/types';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { type Swiper as SwiperCore } from 'swiper';
import { Pagination, Navigation } from 'swiper/modules';

import {
    faChevronLeft,
    faChevronRight,
} from '@fortawesome/pro-solid-svg-icons';
import MrButton from '../base/mr-button/MrButton.vue';
import { type PhotoSizeCollection } from '@clients/HotelApi';
import type { ButtonSize, SizeConditional } from '#imports';

const {
    images = [],
    noSlider,
    noBorderRadius,
    showButtonOnHover,
    hideButtons,
    pagination,
    mobileImageFit = 'cover',
    desktopImageFit = 'cover',
    mobileSlider,
    buttonSize = 'medium',
} = defineProps<IProps>();

interface IProps {
    images?: PhotoSizeCollection[];
    noSlider?: boolean;
    noBorderRadius?: boolean;
    showButtonOnHover?: boolean;
    hideButtons?: boolean;
    pagination?: boolean;
    mobileImageFit?: 'unset' | 'contain' | 'cover' | 'fill' | 'scale-down';
    desktopImageFit?: 'unset' | 'contain' | 'cover' | 'fill' | 'scale-down';
    mobileSlider?: boolean;
    buttonSize?: SizeConditional<ButtonSize>;
    preloadFirstImage?: boolean;
}
const next = ref<InstanceType<typeof MrButton>>();
const prev = ref<InstanceType<typeof MrButton>>();

const swiperActiveIndex = ref(0);
const swiper = ref<SwiperCore>();
const slider = ref<SwiperCore>();
const { isMobile } = useSize();
const mobileSliderEnabled = computed(() => isMobile.value && mobileSlider);
const paginationEnabled = computed(
    () => pagination && !mobileSliderEnabled.value
);
const navButtonsEnabled = computed(
    () => !hideButtons && !mobileSliderEnabled.value
);

const hotelBannerOptions = computed(() => {
    const options: Pick<
        SwiperOptions,
        | 'spaceBetween'
        | 'pagination'
        | 'navigation'
        | 'freeMode'
        | 'touchRatio'
        | 'centeredSlides'
        | 'slidesOffsetAfter'
        | 'slidesOffsetBefore'
        | 'slidesPerView'
    > = {
        spaceBetween: 0,
    };
    if (paginationEnabled.value) {
        options.pagination = {
            clickable: true,
            dynamicBullets: true,
            dynamicMainBullets: 3,
        };
    }

    if (navButtonsEnabled.value) {
        options.navigation = {
            nextEl: next.value?.native,
            prevEl: prev.value?.native,
        };
    }
    if (mobileSliderEnabled.value) {
        options.freeMode = true;
        options.spaceBetween = 4;
        options.touchRatio = 0.2;
        options.centeredSlides = false;
        options.slidesOffsetAfter = 0;
        options.slidesOffsetBefore = 5;
        options.slidesPerView = 'auto';
    }

    return options;
});
const swiperChange = () => {
    if (swiper.value) {
        swiperActiveIndex.value = swiper.value.activeIndex;
        slider.value?.slideToLoop(swiper.value.activeIndex);
    }
};
</script>

<template>
    <div class="image-carousel-container">
        <div
            class="swiper-container"
            :class="{
                'no-slider': noSlider,
                'no-border': noBorderRadius,
                'hover-show-button': showButtonOnHover,
                'mobile-slider': mobileSliderEnabled,
            }"
        >
            <mr-button
                v-if="navButtonsEnabled"
                v-show="swiperActiveIndex !== 0"
                ref="prev"
                class="arrow-button right"
                :size="buttonSize"
                :icon-button="{
                    icon: faChevronRight,
                    color: 'white',
                }"
                @click.stop.prevent
            />
            <client-only>
                <Swiper
                    class="swiper-wrapper"
                    v-bind="hotelBannerOptions"
                    :modules="[Pagination, Navigation]"
                    @active-index-change="swiperChange"
                    @swiper="swiperVal => (swiper = swiperVal)"
                >
                    <SwiperSlide
                        v-for="(image, index) in images"
                        :key="image.blurhash || image.large || `image-${index}`"
                        class="swiper-slide"
                    >
                        <mr-blur-hash-image
                            class="swiper-image"
                            :hash="image.blurhash ?? undefined"
                            :src="image.large || undefined"
                            :image-fit="
                                isMobile ? mobileImageFit : desktopImageFit
                            "
                            :alt="image.alt ?? undefined"
                            :preload="preloadFirstImage && index === 0"
                        />
                    </SwiperSlide>
                </Swiper>
            </client-only>
            <mr-button
                v-if="navButtonsEnabled"
                v-show="swiperActiveIndex !== images.length - 1"
                ref="next"
                class="arrow-button left"
                :size="buttonSize"
                :icon-button="{
                    icon: faChevronLeft,
                    color: 'white',
                }"
                @click.stop.prevent
            />
        </div>
        <div v-show="!noSlider" class="slider-container">
            <client-only>
                <Swiper
                    :modules="[Pagination, Navigation]"
                    :space-between="8"
                    :slides-per-view="5"
                    :touch-ratio="0.2"
                    slide-to-clicked-slide
                    free-mode
                    class="swiper-wrapper"
                    @swiper="swiper => (slider = swiper)"
                    @click="
                        swipe => {
                            swiper?.slideTo(swipe.clickedIndex);
                        }
                    "
                >
                    <SwiperSlide
                        v-for="(image, index) in images"
                        :key="index"
                        class="swiper-slide"
                    >
                        <mr-blur-hash-image
                            :hash="image.blurhash ?? undefined"
                            :src="image.small || undefined"
                            :border-radius="8"
                            :alt="image.alt ?? undefined"
                        />
                    </SwiperSlide>
                </Swiper>
            </client-only>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.image-carousel-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
}

.swiper-button-disabled {
    display: none;
}

.swiper-container {
    height: calc(100% - 97px);
    border-radius: 16px;
    &.no-slider {
        height: 100%;
    }
    &.no-border {
        border-radius: 0;
        .swiper-image :deep() {
            img,
            canvas {
                border-radius: 0 !important;
            }
        }
    }
    &.hover-show-button {
        &:hover {
            .arrow-button {
                opacity: 1;
            }
        }
        .arrow-button {
            transition: all 200ms;
            opacity: 0;
        }
    }
    .swiper-slide {
        width: 100%;
        height: 100%;
    }

    .arrow-button {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 10;

        &.right {
            right: 12px;
        }
        &.left {
            left: 12px;
        }
    }
}

.slider-container {
    width: 100%;
    height: 88px;
    margin-top: 12px;
    .swiper-slide {
        width: 129px;
        height: 85px;
    }

    :deep() {
        img {
            opacity: 1 !important;
        }
    }
}

:deep(.swiper-pagination-bullet) {
    width: 8px;
    height: 8px;
    background: black;
    opacity: 0.2;
    &.swiper-pagination-bullet-active {
        background: white;
        opacity: 1;
    }
}

@media (max-width: $app-mobile-max-width) {
    .image-carousel-container {
        .arrow-button {
            &.right {
                right: 8px;
            }
            &.left {
                left: 8px;
            }
        }
    }
    .swiper-container {
        height: 100%;

        &.mobile-slider {
            @include no-scrollbar;
            overflow-x: scroll;
            .swiper-wrapper {
                width: fit-content;
            }
            .swiper-slide {
                width: 250px !important;
                height: 166px;
                margin-left: 4px;
            }
            .swiper-image :deep() {
                img,
                canvas {
                    border-radius: 8px !important;
                }
            }
        }

        .swiper-image :deep() {
            img,
            canvas {
                border-radius: 0 !important;
            }
        }
    }
}
</style>
